import { useEffect, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Radio, ShieldCheck, AlertTriangle, Loader2, Smartphone } from "lucide-react";
import { Button } from "./ui/button";
import { describePath } from "../lib/connection-path";
import { EXPECTING, FAILED, SERVING, WAITING, hostState, startHosting, stopHosting, watchHost } from "../lib/host-link";
import API from "../lib/api";
import { CONNECT_URL } from "../lib/signalling";
import { useTaal } from "@/contexts/TaalContext";

/**
 * The desktop side: show a code, and then be the machine the phone uses.
 *
 * This is a view, not the connection. The connection lives in host-link.js
 * for as long as the app is open, because this panel sits inside the Settings
 * dialog and closing that dialog used to hang up on the phone: the owner
 * could not look at their own settings without cutting off their own device.
 * Reported and fixed 26 July.
 *
 * So nothing here closes anything on the way out, and opening this panel
 * later shows the phone that is already connected rather than an invitation
 * to connect one.
 */
export default function DirectConnection() {
  const { t } = useTaal();
  const [state, setState] = useState(hostState);

  useEffect(() => watchHost(setState), []);

  const start = () =>
    startHosting({
      // This machine's own backend, signed in as the owner, for the pairing.
      call: async ({ method, path, body }) => {
        const answer = await axios({ method, url: `${API.replace(/\/api$/, "")}${path}`, data: body });
        return answer.data;
      },
    });

  const { where, room, route, busy, log, paired, error, detail, expecting: waitingAtAppointment } = state;

  return (
    <div className="space-y-4" data-testid="direct-connection">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-base font-medium">{t("Novamund op je telefoon")}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {t("Scan een code en je telefoon draait deze Novamund: deze gesprekken, deze Ollama, deze skills. Op elk netwerk, zonder Tailscale en zonder account ergens. Niets ertussen kan lezen wat er overgaat. De verbinding blijft staan als je dit venster sluit.")}
          </p>
        </div>
        {where !== WAITING && where !== SERVING && where !== EXPECTING && (
          <Button size="sm" onClick={start} data-testid="start-direct-btn">
            <Radio className="h-4 w-4 mr-1" />
            {t("Code tonen")}
          </Button>
        )}
      </div>

      {/* A phone whose screen locked, which is most of the day. This Mac is
          standing in the room the two agreed on, so the phone can walk back in
          when it wakes. Deliberately not an error and deliberately without a
          code on it: showing one here would be inviting the owner to fix
          something that is already being fixed. */}
      {where === EXPECTING && (
        <div className="border rounded-xl p-4 space-y-2" data-testid="direct-expecting">
          <p className="text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
            {t("Je telefoon slaapt. Deze Mac houdt de verbinding open voor als hij wakker wordt.")}
          </p>
          <Button variant="ghost" size="sm" onClick={() => stopHosting({ forget: true })}>
            {t("Stop met wachten")}
          </Button>
        </div>
      )}

      {/* Said out loud while a code is on screen, because the opposite used to
          be true and silently so: showing a code abandoned the appointment, so
          the one button the owner could press was the one that shut the door on
          the phone they were trying to reach. Now both rooms are open and the
          screen says which. */}
      {where === WAITING && waitingAtAppointment && (
        <p className="text-[11px] text-muted-foreground" data-testid="direct-also-expecting">
          {t("Je gekoppelde telefoon kan nog steeds terugkomen terwijl deze code in beeld staat.")}
        </p>
      )}

      {where === WAITING && room && (
        <div className="border rounded-xl p-4 space-y-3 text-center" data-testid="direct-waiting">
          <p className="text-sm text-muted-foreground">{t("Scan dit met je telefoon.")}</p>
          <div className="flex justify-center">
            <div className="bg-white p-3 rounded-lg" data-testid="direct-qr">
              <QRCodeSVG value={room.url} size={180} />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground flex items-center justify-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t("Wachten op je telefoon")}
          </p>
          {/* The address is on screen so a wrong code is visible before it is
              scanned. Two codes on one screen was how an afternoon went
              missing on 2026-07-26. */}
          <p className="text-[11px] font-mono text-muted-foreground break-all" data-testid="direct-url">
            {room.url.split("#")[0]}
          </p>
          <p className="text-[10px] text-muted-foreground">
            Introduced by {CONNECT_URL.replace(/^https:\/\//, "")}, which never sees what you send.
          </p>
          <Button variant="ghost" size="sm" onClick={stopHosting}>
            {t("Annuleren")}
          </Button>
        </div>
      )}

      {where === FAILED && (
        <div className="border border-corona/30 rounded-xl p-4 space-y-3" data-testid="direct-error">
          <p className="text-xs text-corona flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            {error}
          </p>
          {detail && (
            <p className="text-[11px] text-muted-foreground" data-testid="direct-detail">
              {detail}
            </p>
          )}
          <Button variant="outline" size="sm" onClick={stopHosting}>
            {t("Opnieuw proberen")}
          </Button>
        </div>
      )}

      {where === SERVING && (
        <div className="border rounded-xl p-4 space-y-3" data-testid="direct-serving">
          <p className="text-xs text-ok flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5" />
            {t("Je telefoon gebruikt deze Mac.")}
          </p>
          {route && (
            <p className="text-[11px] text-muted-foreground" data-testid="direct-route">
              {describePath(route)}
            </p>
          )}
          <p className="text-[11px] text-muted-foreground flex items-center gap-2" data-testid="direct-busy">
            <Smartphone className="h-3 w-3" />
            {busy > 0 ? `Answering ${busy} request${busy === 1 ? "" : "s"}.` : t("Wachten tot hij iets vraagt.")}
          </p>
          <p className="text-[11px] text-muted-foreground" data-testid="direct-paired">
            {paired === null
              ? t("Je telefoon wordt aangemeld...")
              : paired
              ? `Aangemeld als dit account op "${paired}". Je kunt dit intrekken onder Gekoppelde apparaten.`
              : t("Deze telefoon kon niet worden aangemeld. Hij toont een aanmeldscherm.")}
          </p>

          {log.length > 0 && (
            <details className="text-[11px]" data-testid="direct-log">
              <summary className="cursor-pointer text-muted-foreground">
                {t("Wat je telefoon vroeg")}
              </summary>
              {/* Method, path and answer. No bodies and no headers: this is
                  meant to be readable with somebody standing behind you, and
                  it exists because "failed to create new chat" with no way to
                  see the 401 behind it cost an hour on 26 July. */}
              <ul className="mt-2 space-y-1 font-mono max-h-40 overflow-y-auto">
                {log.map((entry, i) => (
                  <li key={i} className="flex gap-2">
                    <span className={entry.status === 200 ? "text-ok" : "text-corona"}>
                      {entry.status}
                    </span>
                    <span className="text-muted-foreground">{entry.method}</span>
                    <span className="truncate">{entry.path}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          <Button variant="ghost" size="sm" onClick={stopHosting}>
            {t("Verbreken")}
          </Button>
        </div>
      )}
    </div>
  );
}
